<template>
  <el-col :span="24" id="view-bankrec" v-loading.body="loading" element-loading-text="Loading...">
    <el-row type="flex" justify="start" align="middle" class="v-padded-s" id="view-bankrec-header">
      <el-col :span="14" class="h-padded left-aligned-col" v-if="bankRec">
        <el-row type="flex" align="middle" justify="start">
          <el-col>
            <el-row type="flex" align="bottom">
              <h2 style="margin: 0; font-weight: 100; display: inline-block;">BANK RECONCILIATION: {{ bank.name }}</h2>
            </el-row>
            <el-row type="flex" align="top">
              {{ bankRec.start }} to {{ bankRec.end }}
            </el-row>
          </el-col>
        </el-row>
      </el-col>
      <el-col :span="10" class="h-padded">
        <el-row type="flex" align="middle" justify="end">
          <el-button plain @click="goBack">
            Back
          </el-button>
          <el-button
            type="primary"
            @click="$router.push({ name: 'BankRecReport', query: { bankrec_id: $route.params.id } })">
            <span v-if="bankRec && bankRec.completed_time">Create Report</span>
            <span v-else>Create Draft Report</span>
          </el-button>
          <el-button
            v-if="bankRec && !bankRec.completed_time"
            :type="form.completed_time ? 'success' : 'primary'"
            @click="update"
            :plain="!saveable || !form.completed_time"
            :disabled="!formHasChanged"
            :loading="isSaving">
            {{ form.completed_time ? 'Post' : 'Save' }}
          </el-button>
        </el-row>
      </el-col>
    </el-row>
    <el-row type="flex" justify="start" align="start" :gutter="20" id="view-bankrec-body">
      <el-col :span="6" style="padding: 12px 0; border-right: 1px solid lightgrey;">
        <el-row v-if="bankRec && bankRec.completed_time" type="flex" justify="center" align="middle" style="padding-right: 12px;">
          <table id="bankrec-stats">
            <tr>
              <td>Start Date</td>
              <td>{{ bankRec.start }}</td>
            </tr>
            <tr>
              <td>End Date</td>
              <td>{{ bankRec.end }}</td>
            </tr>
            <tr>
              <td>Bank</td>
              <td>{{ bank.name }}</td>
            </tr>
            <tr>
              <td>Statement Balance</td>
              <td class="text-right">{{ formatAmount(bankRec.balance_decimal) }}</td>
            </tr>
            <tr>
              <td>Reconciled Amount</td>
              <td class="text-right">{{ formatAmount(reconciledAmount) || '' }}</td>
            </tr>
          </table>
        </el-row>
        <el-row v-else type="flex" justify="center" align="middle" style="padding-right: 12px;">
          <el-col style="padding: 12px 0;">
            <el-row type="flex" justify="center" align="middle" style="padding-right: 12px;">
              <BankRecForm
                :fields="fields"
                :form.sync="form"
                labelPosition="right"
                label-width="150px"
                :idPrefix="'a-'"
                @formChanged="formChanged"
                @selectMenuChanged="selectMenuChanged"
                @fieldFocusChanged="fieldFocusChanged"
              />
            </el-row>
            <el-row type="flex" align="middle" justify="center" style="padding: 24px 12px;">
              <BankRecSelectionSummary
                :selection="selection"
                :currency="currency"
                :getTabLabel="getTabLabel"
              />
            </el-row>
          </el-col>
        </el-row>
      </el-col>
      <el-col :span="18" style="padding: 0;">
        <el-row v-if="reconcilablesCount" type="flex" align="start" :style="`height: ${tableHeight};`">
          <el-col>
            <BankRecTableHeader
              :completed="!!(bankRec.id && bankRec.completed_time)"
              :reconcilables="reconcilables"
              :pageLimit="pageLimit"
              :currentPage="currentPage"
              :activeTabRows="activeTabRows"
              :activeTab="activeTab"
              :changeTabs="changeTabs"
              :changePage="changePage"
            />
            <el-row type="flex">
              <BankRecTable
                :ref="`${activeTab}Table`"
                ref="bankRecTable"
                :completed="!!(bankRec.id && bankRec.completed_time)"
                :activeTab="activeTab"
                :paginatedData="paginatedData"
                :pageLimit="pageLimit"
                :tableReady="tableReady"
                :parseTimestamp="parseTimestamp"
                :showLedgerItem="showLedgerItem"
                :handleSelectionChange="handleSelectionChange"
                :busy="busy"
                @onNewGrid="handleGridUpdate"
              />
            </el-row>
          </el-col>
        </el-row>
        <el-row
          v-else
          type="flex"
          align="middle"
          justify="center"
          :style="`height: ${tableHeight}px;`">
          <el-col :span="12">
            <el-alert v-if="!loading" title="" type="warning" style="margin-top: 24px;" :closable="false" show-icon>
              <template>
                <slot slot-scope="scope">
                  <el-row type="flex" align="middle">
                    <el-col style="margin-left: 6px;" :span="24">
                      <el-row type="flex" justify="start">
                        <span style="font-weight: bold;">Nothing to display</span>
                      </el-row>
                      <el-row type="flex" justify="start" style="padding-top: 6px;">
                        There are no transaction lines or checks associated with this bank reconciliation.
                      </el-row>
                    </el-col>
                  </el-row>
                </slot>
              </template>
            </el-alert>
          </el-col>
        </el-row>
        <el-dialog
          v-if="ledgerItem && ledgerModalVisible"
          :title="ledgerItem.meta_name"
          size="tiny"
          :show-close="false"
          :close-on-click-modal="false"
          :close-on-press-escape="false"
          :visible.sync="ledgerModalVisible">
          <el-row type="flex" justify="center" align="middle">
            <table id="related-transactions-table" cellspacing="0">
              <tr class="ledgeritem-row" v-for="prop in Object.keys(ledgerItem)" :key="`ledger_item_${prop}`">
                <td v-if="!['related', 'meta_name'].includes(prop)">
                  <el-row type="flex" align="middle" justify="end" style="font-weight: bold;">
                    {{ prop.charAt(0).toUpperCase() + prop.slice(1) }}:
                  </el-row>
                </td>
                <td v-if="!['related', 'meta_name'].includes(prop)">
                  <el-row type="flex" align="middle" justify="start">
                    {{ ledgerItem[prop] }}
                  </el-row>
                </td>
                <td v-if="prop === 'related'" valign="top">
                  <el-row type="flex" align="middle" justify="end" style="font-weight: bold;">
                    {{ `Related transaction${ledgerItem.related.length > 1 ? 's' : ''}` }}:
                  </el-row>
                </td>
                <td v-if="prop === 'related'" valign="top">
                  <a
                    v-if="ledgerItem.related.length > 1"
                    v-for="transaction in ledgerItem.related"
                    :key="`related_${transaction.type}_${transaction.id}`"
                    :href="transaction.path"
                    target="_blank"
                    style="display: block;">
                    {{ `${transaction.type} ${transaction.id}` }}
                  </a>
                  <a v-else :href="ledgerItem.related[0].path" target="_blank" style="display: block;">
                    {{ `${ledgerItem.related[0].type} ${ledgerItem.related[0].id}` }}
                  </a>
                </td>
              </tr>
            </table>
          </el-row>
          <span slot="footer" class="dialog-footer">
            <el-button type="primary" @click="ledgerModalVisible = false">Close</el-button>
          </span>
        </el-dialog>
      </el-col>
    </el-row>
  </el-col>
</template>

<script>
  /* eslint-disable camelcase */
  import BankRecForm from '@/components/Programs/BankRecon/BankRecForm.vue'
  import BankRecTable from '@/components/Programs/BankRecon/BankRecTable.vue'
  import BankRecTableHeader from '@/components/Programs/BankRecon/BankRecTableHeader.vue'
  import BankRecSelectionSummary from '@/components/Programs/BankRecon/BankRecSelectionSummary.vue'
  import store from '@/store/store'
  import { mapGetters } from 'vuex'
  import { bankRecMixins } from '@/Mixins/BankRecs'
  import { DataLoader } from '@/utils/helpers/DataLoader/DataLoader'
  import { ManualCheckLoader } from './ManualCheckLoader'
  import { payload as bankActionPayload } from '@/components/RouteComponents/Banks/actionPayload'
  import { formatLocale } from '@/utils/helpers/StringsNumbers'

  export default {
    components: {
      BankRecForm,
      BankRecTable,
      BankRecTableHeader,
      BankRecSelectionSummary
    },
    mixins: [ bankRecMixins ],
    data () {
      return {
        bankRec: null
      }
    },
    methods: {
      /**
       * Formats dollar amounts with thousands separators
       * @param value
       * @return {*}
       */
      formatAmount (value) {
        return value ? formatLocale(value) : ''
      }
    },
    computed: {
      ...mapGetters({
        getBankRecById: 'getBankRecById',
        getBankById: 'getBankById',
        getApById: 'getApById',
        getJeById: 'getJeById',
        getPcById: 'getPcById',
        getCheckById: 'getCheckById'
      })
    },
    async created () {
      this.loading = true
      let promises = []
      if (!this.$store.getters.getBankRecFields) { // Check if the options data exists in the store. If not, fetch it.
        promises.push(this.$store.dispatch('fetchBankRecOptions'))
      }
      let bankRec = this.getBankRecById(this.$route.params.id) // Set the bankrec model in component data prop
      if (!(bankRec && Object.keys(bankRec).length)) {  // Check if the bankrec exists in the store. If not, fetch it.
        promises.push(store.dispatch('fetchBankRecsWithQuery', { query: { id: this.$route.params.id } }))
      }
      try {
        const initResponse = await Promise.all(promises)
        if (initResponse.length) {
          bankRec = store.getters.getBankRecById(this.$route.params.id)
        }
        this.bankRec = bankRec
        promises = [
          this.$store.dispatch('fetchLinesWithQuery', { type: 'ap', query: { recon: bankRec.id } }),
          this.$store.dispatch('fetchLinesWithQuery', { type: 'je', query: { recon: bankRec.id } }),
          this.$store.dispatch('fetchLinesWithQuery', { type: 'pc', query: { recon: bankRec.id } }),
          this.$store.dispatch('fetchAllChecksWithQuery', { recon: bankRec.id }),
          this.$store.dispatch('fetchAllPaymentsWithQuery', { recon: bankRec.id })

        ]
        if (!bankRec.completed_time) {
          promises.push(this.$store.dispatch('fetchBankStatementsByBank', bankRec.bank))
          promises.push(this.$store.dispatch('fetchReconcilables', { type: 'lines/ap', query: { bank: bankRec.bank } }))
          promises.push(this.$store.dispatch('fetchReconcilables', { type: 'lines/je', query: { bank: bankRec.bank } }))
          promises.push(this.$store.dispatch('fetchReconcilables', { type: 'lines/pc', query: { bank: bankRec.bank } }))
          promises.push(this.$store.dispatch('fetchReconcilables', { type: 'checks', query: { bank: bankRec.bank } }))
          promises.push(this.$store.dispatch('fetchReconcilables', { type: 'payments', query: { bank: bankRec.bank } }))
        }
        // Ensure that the banks are available
        let dataLoader = new DataLoader(this.$store, [ bankActionPayload ], true)
        dataLoader.loadData()
          .then(() => {
            this.$store.dispatch('setInitDataLoadStatus', true)
            this.$store.dispatch('setInitDataLoadingStatus', false)
            Promise.all(promises)
              .then(nextResponse => {
                // Fetch manual check references
                const manualCheckLoader = new ManualCheckLoader(store, { id: bankRec.id, apiResponse: nextResponse })
                manualCheckLoader.fetchManualChecks()
                  .then(() => {
                    this.initializeData(nextResponse) // Initialize the API data
                    this.initializeForm() // Initialize the bankrec form
                    this.prevFormState = { ...this.form } // Set previous form state (to track changes)
                    this.getBankBalance()
                    this.loading = false
                  })
              })
          })
      } catch (e) {
        console.log(e)
        this.loading = false
      }
    },
    mounted () {
      const tableHeight = () => {
        const parentHeight = document.getElementById('view-bankrec').offsetHeight
        const headerHeight = document.getElementById('view-bankrec-header').offsetHeight
        return parentHeight - headerHeight
      }
      this.tableHeight = tableHeight()
    }
  }
</script>

<style scoped>
  .focussed-btn {
    background-color: #04709d;
    cursor: default;
  }
  .unfocussed-btn {
    background-color: #fff;
    cursor: pointer;
  }
  #view-bankrec-body {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  #view-bankrec-header {
    border-bottom: 1px solid lightgray;
  }
  #bankrec-stats {
    width: 100%;
  }
  #bankrec-stats td {
    color: #545c64;
    padding: 12px;
  }
  #bankrec-stats tr td:first-child {
    font-weight: bold;
  }
  .h-padded {
    padding: 0 12px;
  }
  .v-padded-s {
    padding-top: 12px;
    padding-bottom: 12px;
  }
  .left-aligned-col {
    text-align: left;
  }
  .selection-counter {
    color: #48576a;
    size: 14px;
    line-height: 1;
  }
  #related-transactions-table {
    width: 100%;
  }
  .ledgeritem-row td:first-child {
    font-weight: bold;
  }
  .ledgeritem-row td:nth-child(2) {
    width: 65%;
  }
  a, a:visited {
    text-decoration: none;
    color: rgb(0, 0, 238) !important;
  }
  #related-transactions-table td{
    padding: 6px;
    border: 1px solid lightgray;
  }
</style>



// WEBPACK FOOTER //
// src/components/Programs/BankRecon/View.vue